import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import { CHECKLIST_ITEM_TEXT_MAX_LENGTH, CHECKLIST_TITLE_MAX_LENGTH } from './checklist';
import { ApiError } from './errors';
import { ulidSchema } from './id';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { publicProfileSchema } from './user';
import { WORKSPACE_NAME_MAX_LENGTH } from './workspace';

/**
 * Home — B76's shared contract for `FR-NOTIF-027` and `FR-NOTIF-028`.
 *
 * Home is a signed-in personal read surface. These schemas are route-specific
 * projections and deliberately do not widen `cardSchema`, `myCardsCardSchema`,
 * `inboxCardSchema`, `commentSchema` or `checklistItemSchema`.
 */

export const HOME_UP_NEXT_LIMIT_DEFAULT = 10;
export const HOME_UP_NEXT_LIMIT_MAX = 50;
export const HOME_HIGHLIGHTS_LIMIT_DEFAULT = 25;
export const HOME_HIGHLIGHTS_LIMIT_MAX = 100;
export const HOME_ASSIGNED_CHECKLIST_ITEMS_LIMIT_DEFAULT = 25;
export const HOME_ASSIGNED_CHECKLIST_ITEMS_LIMIT_MAX = 100;
export const HOME_DUE_SOON_WINDOW_DAYS = 7;

export const HOME_HIGHLIGHT_COMMENT_PREVIEW_MAX_LENGTH = 280;

export const HOME_UP_NEXT_REASONS = [
  'overdue',
  'due_soon',
  'mentioned',
  'added_to_card',
] as const;

export const homeUpNextReasonSchema = z.enum(HOME_UP_NEXT_REASONS);

export type HomeUpNextReason = z.infer<typeof homeUpNextReasonSchema>;

export const homeCursorSchema = z.string().min(1).max(128);

export type HomeCursor = z.infer<typeof homeCursorSchema>;

const HOME_CURSOR_KINDS = ['highlights', 'assignedChecklistItems'] as const;
const homeCursorKindSchema = z.enum(HOME_CURSOR_KINDS);
const POSTGRES_TIMESTAMP_PREFIX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/;
const homeCursorTimestampSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(POSTGRES_TIMESTAMP_PREFIX);
const homeCursorDueAtSchema = homeCursorTimestampSchema.or(z.literal(''));

const homeHighlightsCursorPayloadSchema = z.strictObject({
  kind: z.literal('highlights'),
  createdAt: homeCursorTimestampSchema,
  id: ulidSchema,
});

const homeAssignedChecklistItemsCursorPayloadSchema = z.strictObject({
  kind: z.literal('assignedChecklistItems'),
  dueAt: homeCursorDueAtSchema,
  id: ulidSchema,
});

export type HomeHighlightsCursorPayload = z.infer<
  typeof homeHighlightsCursorPayloadSchema
>;
export type HomeAssignedChecklistItemsCursorPayload = z.infer<
  typeof homeAssignedChecklistItemsCursorPayloadSchema
>;

const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

function assertLatin1(raw: string): void {
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('home cursor components must be latin-1');
    }
  }
}

function malformedHomeCursor(): never {
  throw new ApiError('validation_failed', 'Malformed cursor', {
    cursor: ['must be a cursor returned by a previous Home page'],
  });
}

function encodeHomeCursorPayload(input: {
  readonly kind: (typeof HOME_CURSOR_KINDS)[number];
  readonly sortKey: string;
  readonly id: string;
}): string {
  const raw = `${input.kind}|${input.sortKey}|${input.id}`;
  assertLatin1(raw);
  return toBase64Url(raw);
}

function decodeHomeCursorPayload(cursor: string): {
  readonly kind: (typeof HOME_CURSOR_KINDS)[number];
  readonly sortKey: string;
  readonly id: string;
} {
  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return malformedHomeCursor();
  }

  const parts = raw.split('|');
  if (parts.length !== 3) return malformedHomeCursor();

  const parsed = z
    .strictObject({
      kind: homeCursorKindSchema,
      sortKey: z.string().max(64),
      id: ulidSchema,
    })
    .safeParse({
      kind: parts[0],
      sortKey: parts[1],
      id: parts[2],
    });
  if (!parsed.success) return malformedHomeCursor();

  return parsed.data;
}

export function encodeHomeHighlightsCursor(row: {
  readonly createdAt: string;
  readonly id: string;
}): string {
  return encodeHomeCursorPayload({
    kind: 'highlights',
    sortKey: row.createdAt,
    id: row.id,
  });
}

export function decodeHomeHighlightsCursor(cursor: string): HomeHighlightsCursorPayload {
  const payload = decodeHomeCursorPayload(cursor);
  const parsed = homeHighlightsCursorPayloadSchema.safeParse({
    kind: payload.kind,
    createdAt: payload.sortKey,
    id: payload.id,
  });
  if (!parsed.success) return malformedHomeCursor();
  return parsed.data;
}

export function encodeHomeAssignedChecklistItemsCursor(row: {
  readonly dueAt: string | null;
  readonly id: string;
}): string {
  return encodeHomeCursorPayload({
    kind: 'assignedChecklistItems',
    sortKey: row.dueAt ?? '',
    id: row.id,
  });
}

export function decodeHomeAssignedChecklistItemsCursor(
  cursor: string,
): HomeAssignedChecklistItemsCursorPayload {
  const payload = decodeHomeCursorPayload(cursor);
  const parsed = homeAssignedChecklistItemsCursorPayloadSchema.safeParse({
    kind: payload.kind,
    dueAt: payload.sortKey,
    id: payload.id,
  });
  if (!parsed.success) return malformedHomeCursor();
  return parsed.data;
}

export const homeQuerySchema = z.strictObject({
  upNextLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(HOME_UP_NEXT_LIMIT_MAX)
    .default(HOME_UP_NEXT_LIMIT_DEFAULT),
  highlightsLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(HOME_HIGHLIGHTS_LIMIT_MAX)
    .default(HOME_HIGHLIGHTS_LIMIT_DEFAULT),
  highlightsCursor: homeCursorSchema.optional(),
  highlightsWorkspaceId: ulidSchema.optional(),
  assignedChecklistItemsLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(HOME_ASSIGNED_CHECKLIST_ITEMS_LIMIT_MAX)
    .default(HOME_ASSIGNED_CHECKLIST_ITEMS_LIMIT_DEFAULT),
  assignedChecklistItemsCursor: homeCursorSchema.optional(),
});

export type HomeQuery = z.infer<typeof homeQuerySchema>;

const homeTimestampSchema = z.string().min(1).max(64);

export const homeWorkspaceRefSchema = z.strictObject({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
});

export type HomeWorkspaceRef = z.infer<typeof homeWorkspaceRefSchema>;

export const homeBoardRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  path: z.string().min(1),
  workspace: homeWorkspaceRefSchema,
});

export type HomeBoardRef = z.infer<typeof homeBoardRefSchema>;

export const homeListRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
});

export type HomeListRef = z.infer<typeof homeListRefSchema>;

export const homeCardRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN`, or `null` exactly when `path` carries the short link — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  path: z.string().min(1),
  startAt: homeTimestampSchema.nullable(),
  dueAt: homeTimestampSchema.nullable(),
  completed: z.boolean(),
  board: homeBoardRefSchema,
  list: homeListRefSchema,
});

export type HomeCardRef = z.infer<typeof homeCardRefSchema>;

export const homeNotificationContextSchema = z.strictObject({
  id: ulidSchema,
  type: z.enum(['mentioned', 'added_to_card']),
  createdAt: homeTimestampSchema,
  read: z.literal(false),
});

export type HomeNotificationContext = z.infer<typeof homeNotificationContextSchema>;

const homeUpNextCardRefSchema = homeCardRefSchema.extend({
  completed: z.literal(false),
});

export const homeUpNextItemSchema = z.discriminatedUnion('reason', [
  z.strictObject({
    reason: z.literal('overdue'),
    card: homeUpNextCardRefSchema.extend({ dueAt: homeTimestampSchema }),
  }),
  z.strictObject({
    reason: z.literal('due_soon'),
    card: homeUpNextCardRefSchema.extend({ dueAt: homeTimestampSchema }),
  }),
  z.strictObject({
    reason: z.literal('mentioned'),
    card: homeUpNextCardRefSchema,
    notification: homeNotificationContextSchema.extend({
      type: z.literal('mentioned'),
    }),
  }),
  z.strictObject({
    reason: z.literal('added_to_card'),
    card: homeUpNextCardRefSchema,
    notification: homeNotificationContextSchema.extend({
      type: z.literal('added_to_card'),
    }),
  }),
]);

export type HomeUpNextItem = z.infer<typeof homeUpNextItemSchema>;

export const homeHighlightSchema = z.strictObject({
  id: ulidSchema,
  activityEntryId: ulidSchema,
  commentId: ulidSchema,
  card: homeCardRefSchema,
  actor: publicProfileSchema.nullable(),
  comment: z.strictObject({
    preview: z.string().max(HOME_HIGHLIGHT_COMMENT_PREVIEW_MAX_LENGTH),
    truncated: z.boolean(),
  }),
  createdAt: homeTimestampSchema,
});

export type HomeHighlight = z.infer<typeof homeHighlightSchema>;

export const homeChecklistRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(CHECKLIST_TITLE_MAX_LENGTH),
});

export type HomeChecklistRef = z.infer<typeof homeChecklistRefSchema>;

export const homeAssignedChecklistItemSchema = z.strictObject({
  id: ulidSchema,
  text: z.string().max(CHECKLIST_ITEM_TEXT_MAX_LENGTH),
  checked: z.literal(false),
  dueAt: homeTimestampSchema.nullable(),
  checklist: homeChecklistRefSchema,
  card: homeCardRefSchema,
  checklistItemCount: z.number().int().nonnegative(),
  checklistCheckedItemCount: z.number().int().nonnegative(),
});

export type HomeAssignedChecklistItem = z.infer<typeof homeAssignedChecklistItemSchema>;

export const homeSchema = z.strictObject({
  upNext: z.array(homeUpNextItemSchema).max(HOME_UP_NEXT_LIMIT_MAX),
  highlights: z.array(homeHighlightSchema).max(HOME_HIGHLIGHTS_LIMIT_MAX),
  highlightsNextCursor: homeCursorSchema.nullable(),
  assignedChecklistItems: z
    .array(homeAssignedChecklistItemSchema)
    .max(HOME_ASSIGNED_CHECKLIST_ITEMS_LIMIT_MAX),
  assignedChecklistItemsNextCursor: homeCursorSchema.nullable(),
});

export type Home = z.infer<typeof homeSchema>;
